import React from 'react';
import cn from 'classnames';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';
import InputField from './InputField';

export type Column<T> = {
  id: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
};

type SortDirection = 'asc' | 'desc';

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  keyField: string;
  onRowClick?: (row: T) => void;
  onSort?: (field: string) => void;
  sortField?: string;
  sortDirection?: SortDirection;
  emptyState?: React.ReactNode;
  wrapperClassName?: string;
  tableClassName?: string;
  headerClassName?: string;
  cellClassName?: string;
  pagination?: {
    enabled?: boolean;
    pageSize?: number;
  };
};

const defaultStyles = {
  tableWrapper: 'w-full overflow-x-auto min-w-full divide-y divide-gray-300',
  table: 'w-full border-collapse',
  tableHeader: 'bg-gray-200 border outline-1 outline-offset-[-1px] outline-brand-border',
  tableCell: 'px-4 py-3 border-b border-brand-border text-gray-800 text-sm font-normal font-sans leading-5 align-top',
  tableHeaderButton: 'inline-flex items-center gap-2 text-left text-brand-navy text-sm font-semibold font-sans leading-5 hover:text-blue',
  emptyStateMessage: 'w-full text-center text-brand-navy text-md font-normal font-sans leading-6',
  paginationWrapper: 'flex items-center justify-end gap-3 px-4 py-3',
  paginationButton:
    'flex items-center justify-center rounded-full border border-solid border-gray-300 p-1 text-gray-900 cursor-pointer transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent',
  paginationLabel: 'text-sm text-gray-700',
};

function Table<T extends Record<string, any>>({
  columns,
  rows,
  keyField,
  onRowClick,
  onSort,
  sortField,
  sortDirection = 'asc',
  emptyState = <p className={defaultStyles.emptyStateMessage}>No Records.</p>,
  wrapperClassName,
  tableClassName,
  headerClassName,
  cellClassName,
  pagination,
}: Props<T>) {
  const isPaginationEnabled = Boolean(pagination?.enabled);
  const allowedPageSizes = [5, 10, 20, 30, 40, 50];
  const initialPageSize = pagination?.pageSize && allowedPageSizes.includes(pagination.pageSize) ? pagination.pageSize : 10;
  const [pageSize, setPageSize] = React.useState<number>(initialPageSize);
  const [currentPage, setCurrentPage] = React.useState(1);
  const totalPages = isPaginationEnabled ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;

  React.useEffect(() => {
    if (!isPaginationEnabled) {
      setCurrentPage(1);
      return;
    }
    setCurrentPage((prevPage) => Math.min(Math.max(prevPage, 1), totalPages));
  }, [isPaginationEnabled, totalPages]);

  React.useEffect(() => {
    // keep internal pageSize in sync with prop when it changes
    if (pagination?.pageSize && allowedPageSizes.includes(pagination.pageSize)) {
      setPageSize(pagination.pageSize);
    } else if (pagination?.pageSize && !allowedPageSizes.includes(pagination.pageSize)) {
      setPageSize(10);
    }
  }, [pagination?.pageSize]);

  React.useEffect(() => {
    if (!isPaginationEnabled) {
      setCurrentPage(1);
      return;
    }
    setCurrentPage((prevPage) => Math.min(Math.max(prevPage, 1), totalPages));
  }, [isPaginationEnabled, totalPages]);

  const visibleRows = React.useMemo(() => {
    if (!isPaginationEnabled) return rows;
    const startIndex = (currentPage - 1) * pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [rows, isPaginationEnabled, currentPage, pageSize]);

  return (
    <div className={cn(defaultStyles.tableWrapper, wrapperClassName)}>
      <table className={cn(defaultStyles.table, tableClassName)}>
        <thead className={cn(defaultStyles.tableHeader, headerClassName)}>
          <tr>
            {columns.map(({ id, label, headerClassName: hClass, cellClassName: cClass }, index) => (
              <th
                key={id}
                className={cn(
                  defaultStyles.tableCell,
                  index === 0 && 'py-3.5 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-3',
                  hClass,
                  cellClassName
                )}
              >
                {onSort ? (
                  <button
                    type='button'
                    className={cn(
                      defaultStyles.tableHeaderButton,
                      'border-none bg-transparent uppercase inline-flex items-center gap-1 font-semibold focus:outline-none whitespace-nowrap'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSort(id);
                    }}
                  >
                    <span>{label}</span>
                    <span className='inline-flex w-4 h-4 items-center justify-center flex-shrink-0'>
                      {sortField === id ? sortDirection === 'asc' ? <ArrowUp /> : <ArrowDown /> : null}
                    </span>
                  </button>
                ) : (
                  <span>{label}</span>
                )}
              </th>
            ))}
            <th className={cn(defaultStyles.tableCell, cellClassName)} />
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            visibleRows.map((row) => (
              <tr
                key={String(row[keyField])}
                className={cn('hover:bg-gray-50', onRowClick && 'cursor-pointer')}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map(({ id, render, cellClassName: cClass }) => (
                  <td key={id} className={cn(defaultStyles.tableCell, cClass, cellClassName)}>
                    {render ? render(row) : (row[id] as React.ReactNode) ?? '-'}
                  </td>
                ))}
                <td className={cn(defaultStyles.tableCell)} />
              </tr>
            ))
          ) : (
            <tr>
              <td className={cn(defaultStyles.tableCell)} colSpan={columns.length + 1}>
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isPaginationEnabled && (
        <div className={cn(defaultStyles.paginationWrapper, 'mt-2')} data-testid='table-pagination'>
          <InputField
            name='page-size-select'
            label='Rows per page'
            type='select'
            options={allowedPageSizes.map((s) => String(s))}
            value={String(pageSize)}
            dataTestId='page-size-select'
            onChange={(val) => {
              const newSize = Number(val);
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            inputClassName='border rounded pt-1.5 h-9'
            wrapperClassName='flex items-center gap-2'
            labelClassName='whitespace-nowrap self-center'
          />

          <button
            type='button'
            className={defaultStyles.paginationButton}
            onClick={() => setCurrentPage((prevPage) => Math.max(1, prevPage - 1))}
            disabled={currentPage === 1}
            aria-label='Previous page'
          >
            <ChevronLeft className='w-6 h-6 rotate-180' />
          </button>
          <span className={defaultStyles.paginationLabel}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type='button'
            className={defaultStyles.paginationButton}
            onClick={() => setCurrentPage((prevPage) => Math.min(totalPages, prevPage + 1))}
            disabled={currentPage === totalPages}
            aria-label='Next page'
          >
            <ChevronRight className='w-6 h-6' />
          </button>
        </div>
      )}
    </div>
  );
}

export default Table;
